import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./Wrapper.css";

type WrapperSize = { w: number; h: number };
type WrapperPos = { right: number; bottom: number };

type WrapperProps = {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
};

// default Wrapper

const DEFAULT_SIZE: WrapperSize = { w: 420, h: 560 };
const DEFAULT_POS: WrapperPos = { right: 24, bottom: 24 };

//MARK: props Iniciales

const MOBILE_BREAKPOINT_PX = 768;
const MIN_W_PX = 250;
const MIN_H_PX = 320;
const MAX_W_PX = 1200;
const DESKTOP_MAX_TOP_GAP_PX = 80;

// Funcion limitadora
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

//Tamaños de pantalla actual
const getViewport = () => {
    const width = window.visualViewport?.width ?? window.innerWidth;
    const height = window.visualViewport?.height ?? window.innerHeight;
    return { width, height };
};


//Funcion pantalla o movil
const computeIsMobile = () => {
    if (typeof window === "undefined") return false;
    if (typeof window.matchMedia === "function") {
        return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
    }
    return (window.visualViewport?.width ?? window.innerWidth) <= MOBILE_BREAKPOINT_PX;
};


//Obtener Tamaños permitidos del chat
const clampSizeToViewport = (size: WrapperSize) => {
    const { width: vw, height: vh } = getViewport();

    const minW = Math.min(MIN_W_PX, vw);
    const minH = Math.min(MIN_H_PX, vh);

    const maxW = Math.max(minW, Math.min(MAX_W_PX, vw));
    const maxHWithGap = vh - DESKTOP_MAX_TOP_GAP_PX;
    const maxH = Math.max(minH, maxHWithGap >= minH ? maxHWithGap : vh);

    return {
        w: clamp(size.w, minW, maxW),
        h: clamp(size.h, minH, maxH)
    };
};

// Obtener positions permitidos del chat de acuerdo al tamaño
const clampPosToViewport = (pos: WrapperPos, size: WrapperSize) => {
    const { width: vw, height: vh } = getViewport();
    const maxRight = Math.max(0, vw - size.w);
    const maxBottom = Math.max(0, vh - size.h);
    return {
        right: clamp(pos.right, 0, maxRight),
        bottom: clamp(pos.bottom, 0, maxBottom)
    };
};

export const Wrapper = ({ open, onClose, children }: WrapperProps) => {
    const [size, setSize] = useState<WrapperSize>(DEFAULT_SIZE);
    const [pos, setPos] = useState<WrapperPos>(DEFAULT_POS);
    const [isMobile, setIsMobile] = useState(() => {
        return computeIsMobile();
    });

    const sizeRef = useRef(size);
    const posRef = useRef(pos);
    const isMobileRef = useRef(isMobile);

    const resizing = useRef(false);
    const dragging = useRef(false);

    const startResize = useRef<null | { x: number; y: number; w: number; h: number }>(null);
    const startDrag = useRef<null | { x: number; y: number; right: number; bottom: number }>(null);

    const prevOpen = useRef(open);

    useEffect(() => {
        sizeRef.current = size;
        posRef.current = pos;
        isMobileRef.current = isMobile;
    }, [size, pos, isMobile]);

    useLayoutEffect(() => {
        const wasOpen = prevOpen.current;
        prevOpen.current = open;

        if (!open || wasOpen) return;

        const nextIsMobile = computeIsMobile();
        setIsMobile(nextIsMobile);

        resizing.current = false;
        dragging.current = false;
        startResize.current = null;
        startDrag.current = null;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";

        const nextSize = nextIsMobile ? DEFAULT_SIZE : clampSizeToViewport(DEFAULT_SIZE);
        const nextPos = nextIsMobile ? DEFAULT_POS : clampPosToViewport(DEFAULT_POS, nextSize);
        setSize(nextSize);
        setPos(nextPos);
    }, [open]);

    useEffect(() => {
        if (open) return;
        resizing.current = false;
        dragging.current = false;
        startResize.current = null;
        startDrag.current = null;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
    }, [open]);

    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (isMobileRef.current) return;

            /* Funcion de Rezise */
            if (resizing.current && startResize.current) {
                const dx = e.clientX - startResize.current.x;
                const dy = e.clientY - startResize.current.y;

                const { width: vw, height: vh } = getViewport();
                const availableW = vw - posRef.current.right;
                const availableH = vh - posRef.current.bottom;

                const minW = Math.min(MIN_W_PX, Math.max(0, availableW));
                const minH = Math.min(MIN_H_PX, Math.max(0, availableH));

                const maxW = Math.max(minW, Math.min(MAX_W_PX, availableW));
                const maxHWithGap = vh - DESKTOP_MAX_TOP_GAP_PX;
                const maxH = Math.max(minH, Math.min(availableH, maxHWithGap >= minH ? maxHWithGap : vh));

                const w = clamp(startResize.current.w - dx, minW, maxW);
                const h = clamp(startResize.current.h - dy, minH, maxH);

                setSize({ w, h });
            }

            /* Funcion de arratrar por la pantalla */
            if (dragging.current && startDrag.current) {
                const dx = e.clientX - startDrag.current.x;
                const dy = e.clientY - startDrag.current.y;

                setPos(
                    clampPosToViewport(
                        {
                            right: startDrag.current.right - dx,
                            bottom: startDrag.current.bottom - dy
                        },
                        sizeRef.current
                    )
                );
            }
        };

        const onUp = () => {
            if (!resizing.current && !dragging.current) return;
            resizing.current = false;
            dragging.current = false;
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, []);

    useEffect(() => {
        const onResize = () => {
            const nextIsMobile = computeIsMobile();
            setIsMobile(nextIsMobile);

            if (nextIsMobile) return;

            const nextSize = clampSizeToViewport(sizeRef.current);
            setSize(nextSize);
            setPos(clampPosToViewport(posRef.current, nextSize));
        };

        window.addEventListener("resize", onResize);
        window.visualViewport?.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
            window.visualViewport?.removeEventListener("resize", onResize);
        };
    }, []);

    useEffect(() => {
        if (isMobile) return;
        setPos(currentPos => clampPosToViewport(currentPos, size));
    }, [size, isMobile]);

    const onResizeStart = (e: ReactMouseEvent<HTMLDivElement>) => {
        if (isMobile) return;
        resizing.current = true;

        startResize.current = {
            x: e.clientX,
            y: e.clientY,
            w: size.w,
            h: size.h
        };

        document.body.style.userSelect = "none";
        document.body.style.cursor = "nwse-resize";
    };

    const onDragStart = (e: ReactMouseEvent<HTMLDivElement>) => {
        if (isMobile) return;
        dragging.current = true;

        startDrag.current = {
            x: e.clientX,
            y: e.clientY,
            right: pos.right,
            bottom: pos.bottom
        };

        document.body.style.userSelect = "none";
    };

    if (!open) return null;

    return (
        <div
            className="chat-dock"
            style={
                isMobile
                    ? undefined
                    : {
                        width: size.w,
                        height: size.h,
                        right: pos.right,
                        bottom: pos.bottom
                    }
            }
        >
            <div className="chat-dock-header" onMouseDown={isMobile ? undefined : onDragStart}>
                <span>Chat ERP</span>

                <button
                    type="button"
                    className="chat-dock-close"
                    aria-label="Cerrar chat"
                    onMouseDown={e => e.stopPropagation()}
                    onClick={onClose}
                >
                    ✕
                </button>
            </div>

            {!isMobile && <div className="chat-dock-resize" onMouseDown={onResizeStart} />}

            <div className="chat-dock-body">{children}</div>
        </div>
    );
};
